<template>
  <v-dialog
    :model-value="open"
    @update:model-value="close"
    @after-leave="afterLeave"
    :content-class="dialogContentClass"
    :scrim="scrim"
    :persistent="true"
    :no-click-animation="isDetachedDialog"
    data-cy="historyDialog"
    :fullscreen="xs || fullscreen"
    :max-width="isDetachedDialog ? 400 : 600"
    max-height="800"
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="props"></slot>
    </template>
    <DatePicker
      :devices="selectedDevices"
      :initialDateRangeModel="customDateRangeModel"
      data-cy="datePickerDialog"
      v-if="showDatePicker"
      @done="setCustomDateRanges"
    />
    <v-card v-else>
      <v-card-title class="d-flex align-center">
        <HistoryMenu
          :selectedDevices="selectedDevices"
          :permissions="permissions"
          v-model:passedinParameter="selectedParameter"
          @parameterChange="parameterChange"
        />
        <div class="history-title">{{ $t('map.historyDialog.title') }}</div>
        <v-spacer></v-spacer>
        <div class="popoverIcons">
          <span v-if="!xs">
            <v-btn @click="toggleFullscreen" variant="text" data-cy="toggleFullscreenHistoryDialog">
              <v-icon v-if="fullscreen" color="grey" size="x-large"> mdi-fullscreen-exit </v-icon>
              <v-icon v-else color="grey" size="x-large">mdi-fullscreen</v-icon>
            </v-btn>
          </span>
          <span>
            <v-btn @click="close" variant="text" data-cy="closeHistoryDialog">
              <v-icon color="grey" size="x-large">mdi-close</v-icon>
            </v-btn>
          </span>
        </div>
      </v-card-title>
      <HistoryGraph
        v-if="isOpen"
        ref="historyGraph"
        :isFullscreen="fullscreen"
        :isDetachedDialog="isDetachedDialog"
        :removableDevices="removableDevices"
        :selectedDevices="selectedDevices"
        :selectedParameter="selectedParameter"
        :permissions="permissions"
        :accountId="accountId"
        @close="close"
        @removeDevice="removeDevice"
        @selectCustomDateRange="toggleShowDatePicker"
        @clearCustomDataRangeModel="clearCustomDataRangeModel"
      />
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, nextTick, ref, computed, reactive } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import { getPermissionsByDeviceId } from '../../services/subscriptions'
import { NAMESPACES } from '@/store/types'
import { MAP_GETTERS } from '@/store/map'
import type { RootState } from '@/store'
import type { UnpackedDevice } from '@/services/device-service/devices'
import type { WidgetDevice } from '@/helpers/widgets/helper'
import HistoryGraph from '../history/HistoryGraph.vue'
import HistoryMenu from '../history/HistoryMenu.vue'
import datePicker from '../history/datePicker.vue'
import { useMeasurementOptions } from '@/composables/widgets/useMeasurementOptions'

const MAX_DEVICES = 5

interface DatePickerDonePayload {
  customDateRangeModel: string[]
  customDateRange: { start: Date | null; end: Date | null }
}

export default defineComponent({
  name: 'HistoryDialog',
  setup() {
    const { xs } = useDisplay()
    const store = useStore<RootState>()
    const deviceIds = ref<string[]>([])
    const defaultParameter = computed<number>(
      () => store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`]
    )
    return {
      xs,
      deviceIds,
      ...useMeasurementOptions(reactive({ deviceIds, defaultParameter }))
    }
  },
  components: {
    HistoryGraph: HistoryGraph,
    HistoryMenu: HistoryMenu,
    DatePicker: datePicker
  },
  props: {
    open: Boolean,
    currentDeviceId: String,
    isDetachedDialog: Boolean,
    removableDevices: Boolean,
    scrim: Boolean
  },
  emits: ['close', 'afterLeave'],
  data() {
    return {
      isOpen: false,
      fullscreen: false,
      showDatePicker: false,
      customDateRangeModel: [] as string[]
    }
  },
  mounted() {
    this.updateDeviceList()
  },
  watch: {
    currentDeviceId() {
      this.updateDeviceList()
    },
    open(isOpen: boolean) {
      if (isOpen) {
        this.init()
      }
    },
    allDevices() {
      this.checkDeviceAccess()
    }
  },
  computed: {
    allDevices(): Record<string, UnpackedDevice> {
      return this.$store.state.devices.allDevices
    },
    dialogContentClass(): string {
      return this.xs || this.fullscreen || !this.isDetachedDialog ? '' : 'history-dialog'
    },
    permissions(): Record<string, string[]> {
      const perms: Record<string, string[]> = {}
      for (const device of this.selectedDevices as WidgetDevice[]) {
        const deviceId = device.id
        perms[deviceId] = getPermissionsByDeviceId(deviceId)
      }
      return perms
    }
  },
  methods: {
    async init() {
      // Note: nextTick is required, so that HistoryGraph is initalized with
      // open set to false, and then changed to true. This allows the graph to
      // know when it is opened and to load data
      await nextTick()
      this.isOpen = true
    },
    close() {
      this.$emit('close')
      if (this.fullscreen) {
        this.toggleFullscreen()
      }
    },
    afterLeave() {
      this.isOpen = false
      // clear out list of selected devices
      this.deviceIds = []
      this.$emit('afterLeave')
    },
    async setCustomDateRanges(data: DatePickerDonePayload | undefined): Promise<void> {
      this.toggleShowDatePicker()
      if (data && typeof data === 'object') {
        await nextTick()
        const historyGraph = this.$refs.historyGraph as InstanceType<typeof HistoryGraph>
        historyGraph.setCustomDateRange(data.customDateRange)
        if (data.customDateRangeModel?.length === 2) {
          this.customDateRangeModel = data.customDateRangeModel
        }
      } else {
        this.clearCustomDataRangeModel()
      }
    },
    toggleShowDatePicker() {
      this.showDatePicker = !this.showDatePicker
    },
    toggleFullscreen() {
      this.fullscreen = !this.fullscreen
    },
    parameterChange(newValue: number): void {
      this.selectedParameter = newValue
    },
    removeDevice(deviceIndex: number): void {
      this.deviceIds.splice(deviceIndex, 1)
    },
    checkDeviceAccess() {
      if (Object.keys(this.allDevices).length > 0) {
        const selectedDevices = this.selectedDevices as WidgetDevice[]
        selectedDevices.forEach((item, index) => {
          const device = this.allDevices[item.id]
          if (!device) {
            if (this.selectedDevices.length === 1) {
              this.close()
            } else {
              this.removeDevice(index)
            }
          }
        })
      }
    },
    updateDeviceList() {
      if (this.currentDeviceId !== null && this.deviceIds.length < MAX_DEVICES) {
        // only add unique device ids to selected devices
        let duplicate = false
        for (const deviceId of this.deviceIds) {
          if (deviceId === this.currentDeviceId) {
            duplicate = true
            break
          }
        }

        if (!duplicate) {
          this.deviceIds.push(this.currentDeviceId)
        }
      }
    },
    clearCustomDataRangeModel() {
      this.customDateRangeModel = []
    }
  }
})
</script>

<style lang="scss">
@media (min-width: 400px) {
  .history-dialog {
    position: absolute;
    right: 10px;
    top: 70px;
  }
}
</style>

<style lang="scss" scoped>
.v-dialog {
  .v-card {
    padding: 0 !important;
    .popoverIcons .v-btn {
      min-width: 0 !important;
      width: 36px;
      height: 36px;
    }
  }
  .v-card-title {
    padding: 0;
    font-size: 15px;
    .history-title {
      margin-right: 4px;
      margin-left: 4px;
    }
  }
}
</style>
